import React, { useState, useEffect } from 'react';
import { User, LogOut, Mail, Phone, Building, Calendar, Clock, Lock, X, AlertCircle, CheckCircle, Eye, EyeOff, Unlink, Bell, BellOff } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';
import { usePushNotifications } from '../hooks/usePushNotifications';

const PasswordChangeModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const validatePassword = (pass) => {
        const errors = [];
        if (pass.length < 8) errors.push('At least 8 characters');
        if (!/[A-Z]/.test(pass)) errors.push('One uppercase letter');
        if (!/[a-z]/.test(pass)) errors.push('One lowercase letter');
        if (!/\d/.test(pass)) errors.push('One number');
        return errors;
    };

    const passwordErrors = validatePassword(formData.newPassword);
    const passwordsMatch = formData.newPassword === formData.confirmPassword;
    const isFormValid = formData.currentPassword && passwordErrors.length === 0 && passwordsMatch && formData.confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid) return;

        setLoading(true);
        setError(null);

        try {
            await axios.patch(`${API_BASE_URL}/api/users/me/password`, {
                current_password: formData.currentPassword,
                new_password: formData.newPassword
            }, { withCredentials: true });

            setSuccess(true);
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update password. Please check your current password.');
        } finally {
            setLoading(false);
        }
    };

    const toggleVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <div className="flex items-center gap-2">
                        <Lock className="w-5 h-5 text-blue-600" />
                        <h3 className="text-xl font-bold text-gray-900">Change Password</h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex gap-2 text-sm text-red-700">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {success ? (
                        <div className="py-8 flex flex-col items-center justify-center text-center space-y-4">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-gray-900">Password Changed!</h4>
                                <p className="text-sm text-gray-600">Your security settings have been updated.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Current Password</label>
                                <div className="relative">
                                    <input
                                        type={showPasswords.current ? "text" : "password"}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                        placeholder="••••••••"
                                        value={formData.currentPassword}
                                        onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleVisibility('current')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showPasswords.new ? "text" : "password"}
                                        className={`w-full px-4 py-2 border rounded-lg outline-none transition-all ${formData.newPassword ? (passwordErrors.length === 0 ? 'border-green-300 focus:ring-green-500' : 'border-red-300 focus:ring-red-500') : 'border-gray-300 focus:ring-blue-500'
                                            }`}
                                        placeholder="••••••••"
                                        value={formData.newPassword}
                                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleVisibility('new')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {formData.newPassword && passwordErrors.length > 0 && (
                                    <ul className="text-[10px] grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
                                        {['At least 8 characters', 'One uppercase letter', 'One lowercase letter', 'One number'].map(req => (
                                            <li key={req} className={`flex items-center gap-1 ${passwordErrors.includes(req) ? 'text-gray-400' : 'text-green-600'}`}>
                                                <div className={`w-1 h-1 rounded-full ${passwordErrors.includes(req) ? 'bg-gray-300' : 'bg-green-600'}`} />
                                                {req}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Confirm New Password</label>
                                <div className="relative">
                                    <input
                                        type={showPasswords.confirm ? "text" : "password"}
                                        className={`w-full px-4 py-2 border rounded-lg outline-none transition-all ${formData.confirmPassword ? (passwordsMatch ? 'border-green-300 focus:ring-green-500' : 'border-red-300 focus:ring-red-500') : 'border-gray-300 focus:ring-blue-500'
                                            }`}
                                        placeholder="••••••••"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleVisibility('confirm')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {formData.confirmPassword && !passwordsMatch && (
                                    <p className="text-[10px] text-red-600 mt-1 flex items-center gap-1">
                                        <div className="w-1 h-1 rounded-full bg-red-600" />
                                        Passwords do not match
                                    </p>
                                )}
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!isFormValid || loading}
                                    className={`flex-1 px-4 py-2 rounded-lg text-white font-semibold transition-all ${isFormValid && !loading ? 'bg-blue-600 hover:bg-blue-700 shadow-md' : 'bg-gray-300 cursor-not-allowed'
                                        }`}
                                >
                                    {loading ? 'Changing...' : 'Update Password'}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

const ProfileView = ({ onLogout, allLeads, currentUser }) => {
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    // Google Calendar integration state
    const [calendarStatus, setCalendarStatus] = useState({ connected: false, connected_at: null, org_enabled: false });
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState(null);

    useEffect(() => {
        const fetchCalendarStatus = async () => {
            try {
                const resp = await axios.get(`${API_BASE_URL}/api/auth/google/status`, { withCredentials: true });
                setCalendarStatus(resp.data);
            } catch (err) {
                // Non-critical — user just won't see connection status
                console.warn('Could not fetch Google Calendar status:', err?.response?.status, err?.message);
            }
        };
        fetchCalendarStatus();
    }, []);

    const handleConnectCalendar = async () => {
        setCalendarLoading(true);
        setCalendarError(null);
        try {
            const resp = await axios.get(`${API_BASE_URL}/api/auth/google/authorize`, { withCredentials: true });
            // Redirect to Google consent screen
            window.location.href = resp.data.authorization_url;
        } catch (err) {
            if (err.response?.status === 503) {
                setCalendarError('Google Calendar integration is not enabled by your administrator.');
            } else {
                setCalendarError('Failed to start Google Calendar connection.');
            }
            setCalendarLoading(false);
        }
    };

    const handleDisconnectCalendar = async () => {
        setCalendarLoading(true);
        setCalendarError(null);
        try {
            await axios.delete(`${API_BASE_URL}/api/auth/google/disconnect`, { withCredentials: true });
            setCalendarStatus({ connected: false, connected_at: null });
        } catch (err) {
            setCalendarError('Failed to disconnect Google Calendar.');
        } finally {
            setCalendarLoading(false);
        }
    };

    // Calculate dynamic stats or fall back to defaults if no data
    const totalLeads = allLeads && allLeads.length > 0 ? allLeads.length : 156;
    const leadsConverted = allLeads && allLeads.length > 0
        ? allLeads.filter(l => l.category === 'current_customers').length
        : 43;

    // Simple success rate calculation: Current / Total
    const successRateVal = allLeads && allLeads.length > 0
        ? ((leadsConverted / totalLeads) * 100).toFixed(1)
        : '27.6';

    // Safely parse a datetime string from the backend.
    // The backend may send naive UTC strings (no tz suffix), strings already marked
    // as UTC with 'Z', or strings with a numeric offset (e.g. +09:00 or -05:00).
    // Appending 'Z' to a string that already carries an offset produces an invalid
    // ISO 8601 string and new Date() returns Invalid Date, so we must detect the
    // offset before doing anything.
    const parseDate = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return null;
        // Already has timezone info — 'Z' suffix or ±HH:MM offset
        if (dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
            return new Date(dateStr);
        }
        // Naive datetime from server — assume UTC
        return new Date(dateStr + 'Z');
    };

    // Format Date
    const formatDate = (dateString) => {
        if (!dateString) return 'January 2024';
        try {
            const date = parseDate(dateString);
            if (!date || Number.isNaN(date.getTime())) return 'January 2024';
            return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        } catch (e) {
            return 'January 2024';
        }
    };

    // Format Last Login with relative time
    const formatLastLogin = (dateString) => {
        if (!dateString) return 'Never';
        try {
            const date = parseDate(dateString);
            if (!date || Number.isNaN(date.getTime())) return 'Never';

            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
            if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
            if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

            // For older dates, show full date
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        } catch (e) {
            return 'Never';
        }
    };

    // Get Initials
    const getInitials = (first, last, email) => {
        if (first && last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
        if (email) return email.substring(0, 2).toUpperCase();
        return 'US';
    };

    const user = {
        name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email.split('@')[0] : 'Guest User',
        email: currentUser?.email || 'guest@northai.app',
        role: currentUser?.role || 'Visit Viewer',
        phone: currentUser?.phone_number || 'No phone linked',
        organization: currentUser?.organization_name || 'NorthAI Demo Org',
        joinedDate: formatDate(currentUser?.created_at),
        initials: getInitials(currentUser?.first_name, currentUser?.last_name, currentUser?.email),
        stats: {
            totalVisits: totalLeads,
            leadsConverted: leadsConverted,
            successRate: `${successRateVal}%`
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto bg-gray-50">
            {showPasswordModal && <PasswordChangeModal onClose={() => setShowPasswordModal(false)} />}

            {/* Container with safe padding for TopBar and footer */}
            <div className="max-w-4xl mx-auto p-4 md:p-8 pt-20 md:pt-24 pb-24">
                {/* Profile Header */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 mb-6 transition-all hover:shadow-xl">
                    {/* Banner */}
                    <div className="h-32 bg-gradient-to-r from-blue-600 to-purple-600"></div>

                    {/* Profile Info */}
                    <div className="relative px-6 pb-6">
                        {/* Avatar */}
                        <div className="absolute -top-16 left-6">
                            <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center border-4 border-white shadow-lg group">
                                <span className="text-white text-4xl font-bold group-hover:scale-110 transition-transform">{user.initials}</span>
                            </div>
                        </div>

                        {/* Name and Role */}
                        <div className="pt-20">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">{user.name}</h1>
                            <p className="text-lg text-gray-600 mb-4">{user.role}</p>

                            {/* Contact Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                <div className="flex items-center gap-3 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm">{user.email}</span>
                                </div>
                                <div className="flex items-center gap-3 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                    <Phone className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm">{user.phone}</span>
                                </div>
                                <div className="flex items-center gap-3 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                    <Building className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm">{user.organization}</span>
                                </div>
                                <div className="flex items-center gap-3 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                    <Calendar className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm">Joined {user.joinedDate}</span>
                                </div>
                                <div className="flex items-center gap-3 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors col-span-1 md:col-span-2">
                                    <Clock className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm">Last login: {formatLastLogin(currentUser?.last_login)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:border-blue-200 transition-all">
                        <p className="text-sm text-gray-600 mb-1">Total Visits</p>
                        <p className="text-3xl font-bold text-gray-900">{user.stats.totalVisits}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:border-emerald-200 transition-all">
                        <p className="text-sm text-gray-600 mb-1">Leads Converted</p>
                        <p className="text-3xl font-bold text-emerald-600">{user.stats.leadsConverted}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:border-blue-200 transition-all">
                        <p className="text-sm text-gray-600 mb-1">Success Rate</p>
                        <p className="text-3xl font-bold text-blue-600">{user.stats.successRate}</p>
                    </div>
                </div>

                {/* Integrations — only show when admin has enabled it */}
                {calendarStatus.org_enabled ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Integrations</h2>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">Google Calendar</p>
                                    {calendarStatus.connected ? (
                                        <p className="text-xs text-green-600 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" />
                                            Connected
                                            {calendarStatus.connected_at && (
                                                <span className="text-gray-400 ml-1">
                                                    since {new Date(calendarStatus.connected_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500">Sync confirmed visits to your calendar</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                {calendarStatus.connected ? (
                                    <button
                                        onClick={handleDisconnectCalendar}
                                        disabled={calendarLoading}
                                        className="px-3 py-2 bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 disabled:opacity-50"
                                    >
                                        <Unlink className="w-4 h-4" />
                                        {calendarLoading ? 'Disconnecting...' : 'Disconnect'}
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleConnectCalendar}
                                        disabled={calendarLoading}
                                        className="px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 shadow-md disabled:opacity-50"
                                    >
                                        <Calendar className="w-4 h-4" />
                                        {calendarLoading ? 'Connecting...' : 'Connect'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {calendarError && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg flex gap-2 text-sm text-red-700">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                {calendarError}
                            </div>
                        )}
                    </div>
                ) : null}

                {/* Push Notifications */}
                <NotificationsCard />

                {/* Actions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Account Security & Actions</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                            onClick={() => setShowPasswordModal(true)}
                            className="px-4 py-3 bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                        >
                            <Lock className="w-5 h-5" />
                            Change Password
                        </button>

                        <button
                            onClick={() => alert('Profile settings coming soon!')}
                            className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                        >
                            <User className="w-5 h-5" />
                            Edit Profile
                        </button>

                        <button
                            onClick={onLogout}
                            className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 md:col-span-2 shadow-md hover:shadow-lg"
                        >
                            <LogOut className="w-5 h-5" />
                            Logout
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

function NotificationsCard() {
    const { permission, isSubscribed, isLoading, error, subscribe, unsubscribe, sendTest } = usePushNotifications();
    const unsupported = !('Notification' in window) || !('serviceWorker' in navigator);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-1">
                <Bell className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Push Notifications</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
                Get alerts for new high-priority leads and leads approaching auto-release.
            </p>

            {unsupported ? (
                <p className="text-sm text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-3">
                    Push notifications are not supported in this browser.
                </p>
            ) : permission === 'denied' ? (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                    Notifications are blocked. Enable them in your browser settings, then reload.
                </p>
            ) : (
                <div className="flex items-center gap-3">
                    <button
                        onClick={isSubscribed ? unsubscribe : subscribe}
                        disabled={isLoading}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all
                            ${isSubscribed
                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            } disabled:opacity-50`}
                    >
                        {isSubscribed ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                        {isLoading ? 'Working…' : isSubscribed ? 'Disable Notifications' : 'Enable Notifications'}
                    </button>
                    {isSubscribed && (
                        <button
                            onClick={sendTest}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            Send test
                        </button>
                    )}
                </div>
            )}
            {error && (
                <p className="mt-3 text-sm text-red-600">{error}</p>
            )}
            {isSubscribed && !error && (
                <p className="mt-3 text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Notifications active on this device
                </p>
            )}
        </div>
    );
}

export default ProfileView;
